import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Row, Col, message, Divider } from 'antd';
import { User } from '../../types';
import { CreateUserData, UpdateUserData, createUser, updateUser } from '../../services/userService';

const { Option } = Select;

interface UserFormProps {
  user?: User | null;
  mode?: 'create' | 'edit';
  onSuccess: () => void;
  onCancel?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({ user, mode = 'create', onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        isAppUser: user.isAppUser || false,
        energy: user.energy || 1,
        phoneVerified: user.phoneVerified || false,
        province: user.province || '',
        ward: user.ward || '',
        gender: user.gender || '',
        birthYear: user.birthYear || '',
        refBy: user.refBy || '',
      });
    } else {
      form.resetFields();
    }
  }, [user, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (user) {
        // Update existing user - map to admin API format
        const userData: UpdateUserData = {
          name: values.name,
          email: values.email || null,
          phone: values.phone,
          isAppUser: values.isAppUser,
          energy: values.energy ? parseInt(values.energy) : undefined,
        };
        await updateUser(user.id, userData);
        message.success('User updated successfully');
      } else {
        // Create new user - map to admin API format
        const userData: CreateUserData = {
          name: values.name,
          email: values.email || null,
          phone: values.phone,
          isAppUser: values.isAppUser || false,
          energy: values.energy ? parseInt(values.energy) : 1,
        };
        await createUser(userData);
        message.success('User created successfully');
      }
      
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      autoComplete="off"
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: 'Please input full name!' },
              { min: 2, message: 'Name must be at least 2 characters!' },
            ]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              { required: true, message: 'Please input phone number!' },
            ]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="email"
            label="Email"
          >
            <Input placeholder="Enter email (optional)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="isAppUser"
            label="User Type"
          >
            <Select placeholder="Select user type">
              <Option value={true}>App User</Option>
              <Option value={false}>Web User</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="energy"
            label="Energy"
            rules={[
              { type: 'number', min: 0, message: 'Energy must be at least 0!' },
            ]}
          >
            <Input type="number" placeholder="Enter energy level" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phoneVerified"
            label="Phone Verified"
          >
            <Select placeholder="Phone verification status">
              <Option value={true}>Verified</Option>
              <Option value={false}>Unverified</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider>Additional Information</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="province"
            label="Province"
          >
            <Input placeholder="Enter province (optional)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="ward"
            label="Ward"
          >
            <Input placeholder="Enter ward (optional)" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="gender"
            label="Gender"
          >
            <Select placeholder="Select gender (optional)" allowClear>
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="birthYear"
            label="Birth Year"
          >
            <Input type="number" placeholder="Enter birth year (optional)" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="refBy"
            label="Referred By"
          >
            <Input placeholder="Enter referrer code (optional)" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
            >
              {mode === 'edit' ? 'Update User' : 'Create User'}
            </Button>
          </Col>
          <Col span={12}>
            <Button 
              onClick={onCancel}
              block
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

export default UserForm;
